import { Template } from '../_types';

export interface ValidationResults {
  inactiveFieldIds: Set<number>;
  duplicateFieldIds: Set<number>;
  duplicateSectionIds: Set<number>;
  emptySectionIds: Set<number>;
  removableChildFieldIds: Set<number>;
}

/**
 * Validates a template and returns sets of IDs for fields and sections with issues.
 * Used for both publish validation and auto-save re-validation to provide consistent
 * validation logic across the template builder.
 *
 * NOTE: Only validates active (non-deleted) sections and fields.
 * Soft-deleted items (isDeleted or effectiveIsDeleted = true) are excluded from validation.
 */
export const validateTemplate = (template: Template): ValidationResults => {
  const inactiveFieldIds = new Set<number>();
  const duplicateFieldIds = new Set<number>();
  const duplicateSectionIds = new Set<number>();
  const emptySectionIds = new Set<number>();

  // Filter out deleted sections first
  const activeSections = template.templateSections.filter(
    (section) => !section.isDeleted && !section.effectiveIsDeleted
  );

  // Validate inactive fields (only check active fields in active sections)
  activeSections.forEach((section) => {
    section.sectionFields.forEach((field) => {
      // Only validate fields that are not deleted
      if (!field.isDeleted && !field.effectiveIsDeleted && field.fieldStatus === 'Inactive') {
        inactiveFieldIds.add(field.id);
      }
    });
  });

  // Validate duplicate fields (same fieldId used multiple times in active sections)
  const fieldIdMap = new Map<number, number[]>();
  activeSections.forEach((section) => {
    section.sectionFields.forEach((field) => {
      // Only check active fields
      if (!field.isDeleted && !field.effectiveIsDeleted) {
        if (!fieldIdMap.has(field.fieldId)) {
          fieldIdMap.set(field.fieldId, []);
        }
        fieldIdMap.get(field.fieldId)!.push(field.id);
      }
    });
  });

  fieldIdMap.forEach((templateFieldIds) => {
    if (templateFieldIds.length > 1) {
      // Add all template field IDs that share the same fieldId
      templateFieldIds.forEach((id) => duplicateFieldIds.add(id));
    }
  });

  // Validate duplicate section names (case-insensitive, only active sections)
  const sectionNameMap = new Map<string, number[]>();
  activeSections.forEach((section) => {
    const lowerName = section.effectiveName.toLowerCase();
    if (!sectionNameMap.has(lowerName)) {
      sectionNameMap.set(lowerName, []);
    }
    sectionNameMap.get(lowerName)!.push(section.id);
  });

  sectionNameMap.forEach((sectionIds) => {
    if (sectionIds.length > 1) {
      // Add all section IDs that share the same name
      sectionIds.forEach((id) => duplicateSectionIds.add(id));
    }
  });

  // Validate empty sections (sections with no active fields)
  activeSections.forEach((section) => {
    const activeFields = section.sectionFields.filter((field) => !field.isDeleted && !field.effectiveIsDeleted);
    if (activeFields.length === 0) {
      emptySectionIds.add(section.id);
    }
  });

  return {
    inactiveFieldIds,
    duplicateFieldIds,
    duplicateSectionIds,
    emptySectionIds,
    removableChildFieldIds: getRemovableChildFieldsInLockedSections(template),
  };
};

/**
 * Identifies child-specific fields that are in sections locked by the parent template.
 * These fields can be removed or moved even though the section is locked, because
 * they were added by the child template, not inherited from the parent.
 *
 * A field is removable when:
 * 1. The section's parent section is locked (checked via parentTemplateSectionId)
 * 2. The field is child-specific (parentSectionFieldId === null)

 */
export const getRemovableChildFieldsInLockedSections = (template: Template): Set<number> => {
  const removableFieldIds = new Set<number>();

  if (!template.parentTemplate) return removableFieldIds;
  const lockedParentSectionIds = new Set(
    template.parentTemplate.templateSections.filter((ps) => ps.effectiveIsLocked).map((ps) => ps.id)
  );

  if (lockedParentSectionIds.size === 0) return removableFieldIds;

  // Filter out deleted sections
  const activeSections = template.templateSections.filter(
    (section) => !section.isDeleted && !section.effectiveIsDeleted
  );

  activeSections.forEach((section) => {
    // Check if the section is inherited and its parent is locked
    if (section.parentTemplateSectionId !== null && lockedParentSectionIds.has(section.parentTemplateSectionId)) {
      // Find child-specific fields (not inherited from parent)
      section.sectionFields.forEach((field) => {
        if (
          field.parentSectionFieldId === null &&
          !field.isDeleted &&
          !field.effectiveIsDeleted
        ) {
          removableFieldIds.add(field.id);
        }
      });
    }
  });

  return removableFieldIds;
};
